import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { listPublishedTemplates } from './templateRepository';

const pageSx = {
  bgcolor: 'grey.50',
  color: 'text.primary',
  minHeight: 'calc(100vh - 64px)',
  textAlign: 'left',
};

const containerSx = {
  width: '100%',
  maxWidth: 1120,
  px: { xs: 2, sm: 3, md: 4 },
  py: { xs: 3, sm: 4, md: 6 },
};

export default function TemplatesPage() {
  const [state, setState] = useState({ loading: true, templates: [], error: '' });

  useEffect(() => {
    let active = true;
    listPublishedTemplates()
      .then((templates) => active && setState({ loading: false, templates, error: '' }))
      .catch(() => active && setState({ loading: false, templates: [], error: 'Templates could not be loaded. Please try again.' }));
    return () => { active = false; };
  }, []);

  const templateCount = state.templates.length;

  return (
    <Box sx={pageSx} data-testid="templates-page">
      <Container maxWidth={false} sx={containerSx}>
        <Paper
          component="section"
          aria-labelledby="templates-title"
          variant="outlined"
          data-testid="templates-hero"
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 2,
            borderTop: 6,
            borderTopColor: 'primary.main',
            boxShadow: 1,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2.5, sm: 4 }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
                Utilities · Templates
              </Typography>
              <Typography
                id="templates-title"
                variant="h3"
                component="h1"
                sx={{
                  mt: 0.5,
                  maxWidth: '20ch',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  fontWeight: 850,
                  lineHeight: 1.08,
                  letterSpacing: '-0.025em',
                  overflowWrap: 'anywhere',
                }}
              >
                Start with a journey template
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ mt: 2, maxWidth: '68ch', fontSize: { xs: '1rem', sm: '1.075rem' }, lineHeight: 1.7 }}
              >
                Browse practical starting points, preview every step, and sign in only when you are ready to make one your own.
              </Typography>
            </Box>
            <Box
              aria-hidden="true"
              sx={{
                width: 72,
                height: 72,
                flex: '0 0 72px',
                display: 'grid',
                placeItems: 'center',
                borderRadius: 2,
                color: 'primary.main',
                bgcolor: 'action.selected',
                border: 1,
                borderColor: 'primary.light',
              }}
            >
              <CollectionsBookmarkOutlinedIcon sx={{ fontSize: 36 }} />
            </Box>
          </Stack>
        </Paper>

        <Box component="section" aria-labelledby="template-gallery-heading" sx={{ mt: { xs: 5, md: 7 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ mb: 3, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}
          >
            <Box>
              <Typography id="template-gallery-heading" variant="h4" component="h2" sx={{ fontWeight: 850, letterSpacing: '-0.015em' }}>
                Choose a starting point
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Open a template to review its goal, steps, and actions before using it.
              </Typography>
            </Box>
            {!state.loading && !state.error ? (
              <Chip
                label={`${templateCount} ${templateCount === 1 ? 'template' : 'templates'}`}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            ) : null}
          </Stack>

          {state.loading ? (
            <Paper variant="outlined" data-testid="templates-loading-state" sx={{ borderRadius: 2, py: { xs: 6, sm: 8 } }}>
              <Stack spacing={2} alignItems="center">
                <CircularProgress aria-label="Loading templates" />
                <Typography color="text.secondary">Loading journey templates…</Typography>
              </Stack>
            </Paper>
          ) : null}

          {state.error ? <Alert severity="error" sx={{ borderRadius: 2 }}>{state.error}</Alert> : null}

          {!state.loading && !state.error && templateCount === 0 ? (
            <Paper
              variant="outlined"
              data-testid="templates-empty-state"
              sx={{ borderRadius: 2, borderStyle: 'dashed', py: { xs: 6, sm: 8 }, px: 3, textAlign: 'center' }}
            >
              <CollectionsBookmarkOutlinedIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="h5" component="h3" sx={{ mt: 1.5, fontWeight: 800 }}>
                No templates are available yet
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 560, mx: 'auto', lineHeight: 1.65 }}>
                New starting points will appear here when they are ready to use.
              </Typography>
            </Paper>
          ) : null}

          {!state.loading && !state.error && templateCount > 0 ? (
            <Grid container spacing={2.5} data-testid="template-gallery">
              {state.templates.map((template) => (
                <Grid item xs={12} sm={6} md={4} key={template.templateId}>
                  <Card
                    component="article"
                    variant="outlined"
                    data-testid="template-card"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      borderTop: 4,
                      borderTopColor: 'primary.main',
                      boxShadow: 1,
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2.5, sm: 3 }, flexGrow: 1 }}>
                      <Chip
                        label={template.category}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mb: 1.5, maxWidth: '100%', fontWeight: 700 }}
                      />
                      <Typography variant="h5" component="h3" sx={{ fontWeight: 800, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
                        {template.title}
                      </Typography>
                      <Typography color="text.secondary" sx={{ mt: 1.25, lineHeight: 1.65, overflowWrap: 'anywhere' }}>
                        {template.description}
                      </Typography>
                      <Box
                        sx={{ mt: 2.5, px: 2, py: 1.5, bgcolor: 'action.hover', borderLeft: 3, borderLeftColor: 'primary.main', borderRadius: 1 }}
                      >
                        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', fontWeight: 800, lineHeight: 1.3 }}>
                          Goal
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 650, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                          {template.goal}
                        </Typography>
                      </Box>
                    </CardContent>
                    <CardActions sx={{ px: { xs: 2.5, sm: 3 }, pb: { xs: 2.5, sm: 3 }, pt: 0 }}>
                      <Button
                        component={RouterLink}
                        to={`/templates/${template.templateId}`}
                        variant="outlined"
                        endIcon={<ArrowForwardIcon />}
                        sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' }, fontWeight: 700 }}
                      >
                        Preview template
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : null}
        </Box>
      </Container>
    </Box>
  );
}
